import React, { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRight, Plug } from "lucide-react";
import { toast } from "sonner";
import { logger } from "@/services/LoggingService";
import { BaseWealthicaConnect } from "@/providers/wealthica2/components/BaseWealthicaConnect";
import { useMultiWealthica2Store } from '@/stores/multiWealthica2Store';
import Wealthica2CallbackHandler from "@/providers/wealthica2/components/Wealthica2CallbackHandler";
import { useWealthica2Navigation } from "@/providers/wealthica2/context/navigation/NavigationProvider";
import { WealthicaConnectionStatusDialog } from "@/components/investments/WealthicaConnectionStatusDialog";
import { useWealthicaReconnectOptimized } from "@/providers/wealthica2/hooks/useWealthicaReconnectOptimized";
import { ConnectionFlowState } from "@/providers/wealthica2/utils/flow-state";
import { fetchInstitutionMetadata } from '@/providers/wealthica2/store/db-io';
import { SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { ChevronRight } from "lucide-react";
import { PageContainer } from "@/components/ui/PageContainer";

function ConnectInvestmentsContent() {
  // --- Only Wealthica and Manual Entry are functional ---
  // --- Digital Assets card is UI only (button disabled), no Vezgo/crypto logic present ---
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [hasDetectedParams, setHasDetectedParams] = useState(false);
  const [showConnectionStatusDialog, setShowConnectionStatusDialog] = useState(false);
  const [dialogConnections, setDialogConnections] = useState<Array<{
    id: string;
    institutionId: string;
    institutionName: string;
    providerName: string;
    connectionStatus: string;
    logoUrl?: string | null;
  }>>([]);
  
  // Check if this is an OAuth callback (used later in render)
  const institution = searchParams.get('institution') || searchParams.get('code');

  // Cutover: multi-store only
  
  // Multi-store only: check if any connection exists and compute status
  const multiStore = useMultiWealthica2Store();
  const humanizeIdentifier = (id?: string | null) => {
    if (!id) return 'Institution';
    try {
      const spaced = String(id).replace(/[-_]+/g, ' ').trim();
      return spaced.replace(/\b\w/g, (m) => m.toUpperCase());
    } catch {
      return String(id);
    }
  };
  const connectionData = multiStore.activeConnectionId ?
    (multiStore.getConnection(multiStore.activeConnectionId)?.connectionData || null) : null;
  const status = multiStore.activeConnectionId ? 
    (multiStore.getConnection(multiStore.activeConnectionId)?.status || 'idle') : 'idle';
  const isLoadingConnections = multiStore.isInitializing || !multiStore.hydrationCompleted || multiStore.isFetchingAllConnections;
  // Build dialog connections enriched with institution metadata (name/logo)
  useEffect(() => {
    const load = async () => {
      try {
        const entries = Array.from(multiStore.connections.values());
        const results = await Promise.all(entries.map(async (c) => {
          const isDemo = c.providerName === 'demo' || c.connectionData?.institutionIdentifier === 'demo';
          const lookupIdentifier = isDemo ? 'demo' : (c.connectionData?.institutionIdentifier || c.institutionId);
          const providerType: 'demo' | 'wealthica' = isDemo ? 'demo' : 'wealthica';
          let meta: Awaited<ReturnType<typeof fetchInstitutionMetadata>> | null = null;
          try { meta = await fetchInstitutionMetadata(lookupIdentifier, providerType); } catch { meta = null; }
          return {
            id: c.connectionData?.connectionId || c.institutionId,
            institutionId: c.institutionId,
            institutionName: meta?.institution_name || humanizeIdentifier(lookupIdentifier),
            providerName: c.providerName,
            connectionStatus: c.status,
            logoUrl: meta?.logo_proxied_url || meta?.logo_url || undefined
          };
        }));
        setDialogConnections(results);
      } catch (e) {
        // Non-fatal; leave fallbacks
        setDialogConnections([]);
      }
    };
    load();
  }, [multiStore.connections]);
  
  const navigation = useWealthica2Navigation();
  const { reconnectAccount } = useWealthicaReconnectOptimized({ connectionData });


  // Dialog connections derived from multi-store (no DB hook)

  useEffect(() => {
    const institution = searchParams.get('institution') || searchParams.get('code');
    const provider = searchParams.get('provider');
    const errorMsg = searchParams.get('error');
    
    logger.info('ConnectInvestments', 'URL parameters detected', {
      institution,
      provider,
      errorMsg,
      url: location.pathname + location.search
    });
    
    if (errorMsg) {
      toast.error(`Connection error: ${errorMsg}`);
      
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('error');
      navigate(cleanUrl.pathname + cleanUrl.search, { replace: true });
      return;
    }
    
    // Just mark that we detected parameters (actual processing happens in Wealthica2Callback)
    setHasDetectedParams(true);
  }, [location, navigate, searchParams]);

  // Check for existing connection when page loads (but not during callback processing)
  useEffect(() => {
    const institution = searchParams.get('institution') || searchParams.get('code');
    
    // Don't show dialog during callback processing
    if (institution) {
      logger.debug('ConnectInvestments', 'Skipping connection dialog - callback processing in progress', {
        institution,
        url: location.pathname + location.search
      });
      return;
    }
    
    // Start connect page flow to prevent auto-reconnection interference
    ConnectionFlowState.startUserFlow('connect');
    
    // Enhanced debugging for multi-store connection dialog detection
    const multiStoreConnections = Array.from(multiStore.connections.entries());
    const hasMultiStoreConnections = multiStoreConnections.length > 0;
    
    logger.info('ConnectInvestments', 'MULTI-STORE CONNECTION DIALOG DEBUG', {
      storeMode: 'multi-store',
      hasConnectionData: !!connectionData,
      connectionStatus: status,
      institutionId: connectionData?.institutionId,
      providerName: connectionData?.providerName,
      connectionDataKeys: connectionData ? Object.keys(connectionData) : [],
      multiStoreActiveId: multiStore.activeConnectionId,
      multiStoreConnectionCount: multiStoreConnections.length,
      multiStoreConnectionIds: multiStoreConnections.map(([id, conn]) => ({ id, status: conn.status })),
      hasMultiStoreConnections,
      currentShowDialogState: showConnectionStatusDialog,
      timestamp: new Date().toISOString()
    });
    
    // FIXED: Enhanced condition checking for existing connections in both modes
    let hasValidConnection = false;
    
    // Multi-store mode: check if we have any valid connections
    hasValidConnection = hasMultiStoreConnections && 
                        multiStoreConnections.some(([id, conn]) => 
                          conn.status === 'connected' && conn.connectionData?.institutionId
                        );
    
    if (hasValidConnection) {
      logger.info('ConnectInvestments', 'VALID CONNECTION DETECTED - SHOWING DIALOG', {
        storeMode: 'multi-store',
        institutionId: connectionData?.institutionId,
        providerName: connectionData?.providerName,
        status: status,
        multiStoreConnectionCount: multiStoreConnections.length,
        dialogWillShow: true
      });
      setShowConnectionStatusDialog(true);
    } else {
      logger.debug('ConnectInvestments', 'NO VALID CONNECTION - DIALOG HIDDEN', {
        storeMode: 'multi-store',
        hasConnectionData: !!connectionData,
        status: status,
        hasInstitutionId: connectionData?.institutionId,
        hasMultiStoreConnections,
        showingDialog: false,
        reasonForHiding: !hasMultiStoreConnections ? 'no_multistore_connections' :
                        !connectionData ? 'no_connection_data' : 
                        status !== 'connected' && status !== 'idle' ? 'wrong_status' :
                        !connectionData.institutionId ? 'no_institution_id' : 'unknown'
      });
    }
    
    return () => {
      ConnectionFlowState.endUserFlow();
    };
  }, [connectionData, status, multiStore.connections, multiStore.activeConnectionId, searchParams, location]);

  // Get auth info for user ID in connection process (must be called unconditionally)
  const { user } = useAuthStore();

  const handleViewInvestments = () => {
    setShowConnectionStatusDialog(false);
    navigate('/investments');
  };

  const handleUpdateAccounts = () => {
    setShowConnectionStatusDialog(false);
    reconnectAccount();
  };

  const handleAddNewAccount = () => {
    setShowConnectionStatusDialog(false);
    // Continue with normal connection flow for multiple institutions
    // This supports the intended behavior of adding additional financial institutions
  };

  // If this is an OAuth callback, render only the callback handler and suppress page chrome
  if (institution) {
    return <Wealthica2CallbackHandler />;
  }

  return (
      <div className="flex-1">
        <SidebarInset>
          <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4 sticky top-0 bg-background z-10">
            <SidebarTrigger className="-ml-2" />
            <Separator orientation="vertical" className="h-6" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/">Dashboard</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator>
                  <ChevronRight className="h-4 w-4" />
                </BreadcrumbSeparator>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/investments">Investments</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator>
                  <ChevronRight className="h-4 w-4" />
                </BreadcrumbSeparator>
                <BreadcrumbItem>
                  <BreadcrumbPage>Connect</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </header>
          
          <PageContainer maxWidthClassName="max-w-[960px]">
            {/* Title and subtitle area */}
            <div className="flex flex-col mb-4">
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold">Connect Accounts</h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    Connect to view investment accounts and digital assets.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Provider cards (no callback rendering here; callback handled headlessly above) */}
            <div className="grid md:grid-cols-2 gap-6">
                {/* Wealthica Card */}
                <Card className="transition-all hover:shadow-md flex flex-col">
                  <CardHeader className="pb-6">
                    <CardTitle className="text-lg font-semibold mb-3">Canadian Accounts</CardTitle>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>Powered by</span>
                      <a 
                        href="https://wealthica.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="bg-gradient-to-r from-[#8B5CF6] to-[#A855F7] rounded px-2 py-1 border border-transparent hover:shadow-md transition-all"
                      >
                        <span className="font-medium text-white">Wealthica</span>
                      </a>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col">
                    <p className="text-sm text-muted-foreground mb-6 flex-1">
                      Wealthica powers secure connections to Canadian investment accounts from brokerages and leading wealth management firms.
                    </p>
                    <BaseWealthicaConnect
                      variant="inline"
                      reference={user?.id}
                      className="w-full group mt-auto gap-1"
                    >
                      <Plug className="h-4 w-4" />
                      Connect Account
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </BaseWealthicaConnect>
                  </CardContent>
                </Card>

                {/* Digital Assets Card */}
                <Card className="transition-all hover:shadow-md opacity-60 flex flex-col">
                  <CardHeader className="pb-6">
                    <CardTitle className="text-lg font-semibold mb-3">Digital Assets</CardTitle>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>Powered by</span>
                      <a 
                        href="https://vezgo.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="bg-gradient-to-r from-[#8B5CF6] to-[#F97316] rounded px-2 py-1 border border-transparent hover:shadow-md transition-all"
                      >
                        <span className="font-medium text-white">Vezgo</span>
                      </a>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col">
                    <p className="text-sm text-muted-foreground mb-6 flex-1">
                      Vezgo powers our secure connections to digital asset account from crypto currencies, blockchain assets and global exchanges.
                    </p>
                    <Button className="w-full group mt-auto" disabled>
                      View Digital Assets
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </CardContent>
                </Card>
            </div>
            
            {/* Connection Status Dialog */}
            {connectionData && !isLoadingConnections && dialogConnections && dialogConnections.length > 0 && (
              <WealthicaConnectionStatusDialog
                open={showConnectionStatusDialog}
                onOpenChange={setShowConnectionStatusDialog}
                connections={dialogConnections}
                onViewInvestments={handleViewInvestments}
                onUpdateAccounts={(connection: any) => handleUpdateAccounts()}
                onAddNewAccount={handleAddNewAccount}
              />
            )}
          </PageContainer>
        </SidebarInset>
      </div>
  );
}

export default function ConnectInvestments() {
  return <ConnectInvestmentsContent />;
}
